import classNames from 'classnames';
import gsap from 'gsap';
import { observer } from 'mobx-react-lite';
import { useCallback, useLayoutEffect, useState } from 'react';
import { useStoreUIV2 } from '../../hooks/useStore.hook';
import { Icon } from '../icon/icon.component';
import { Menu, MenuType } from '../menu/menu.component';
import './nav.component.scss';

export declare namespace NavType {
  type Props = {
    className?: string | null;
    data?: NavType.Data[];
    onNavigate?: (value: string) => void;
    logo?: React.ReactNode | null;
  };

  type Data = {
    key: string;
    position: 'top' | 'bottom';
    title?: string | null;

    items: MenuType.Data.Item[];
  };
}

const NavCollapse = observer(({ onCollapse }: { onCollapse: () => void }) => {
  return (
    <div onClick={onCollapse} id="navCollapse" className="navCollapse">
      <Icon variant="collapse" color="deep_indigo" size="m" />
    </div>
  );
});

const EASE: gsap.EaseString = 'power1.inOut';
const DURATION = 0.3;
const STORAGE_KEY = 'nav-retract';

export const Nav = observer(
  ({ data = [], className, onNavigate, logo }: NavType.Props) => {
    const { SSOStore } = useStoreUIV2();
    const [isRetract, setIsRetract] = useState<boolean>(
      localStorage.getItem(STORAGE_KEY) === 'true',
    );

    const setRetract = useCallback(
      (isRetract: boolean) => {
        localStorage.setItem(STORAGE_KEY, isRetract.toString());
        setIsRetract(isRetract);
      },
      [setIsRetract],
    );

    const setFull = useCallback(() => {
      const navFull = document.getElementById('navFull');
      const navRetract = document.getElementById('navRetract');

      if (!navFull || !navRetract) return;

      const navRetractWidth = navRetract?.getBoundingClientRect().width;

      const timeline = gsap.timeline({ ease: EASE });

      timeline
        .set(navRetract, { pointerEvents: 'none' })
        .to(navRetract, {
          opacity: 0,
          marginLeft: `-${navRetractWidth}px`,
        })
        .to(
          [navFull, navFull.querySelectorAll('*:not(#navCollapse)')],
          {
            marginLeft: 0,
            opacity: 1,
          },
          '<',
        )
        .set(navFull, {
          pointerEvents: 'visible',
        })
        .eventCallback('onComplete', () => {
          setRetract(false);
        })
        .duration(DURATION);
    }, [gsap]);

    const setMinimize = useCallback(() => {
      const navFull = document.getElementById('navFull');
      const navRetract = document.getElementById('navRetract');

      if (!navFull || !navRetract) return;

      const navFullWidth = navFull?.getBoundingClientRect().width;
      const navRetractWidth = navRetract?.getBoundingClientRect().width;

      const timeline = gsap.timeline({ ease: EASE });

      timeline
        .set(navFull, {
          pointerEvents: 'none',
        })
        .to(navFull.querySelectorAll('*:not(#navCollapse)'), {
          opacity: 0,
        })
        .to(
          navFull,
          { marginLeft: `-${navFullWidth - navRetractWidth}px` },
          '<',
        )
        .to(navFull, { opacity: 0 }, '<')
        .to(navRetract, { opacity: 1 }, '<')
        .set(navRetract, {
          pointerEvents: 'visible',
        })
        .eventCallback('onComplete', () => {
          setRetract(true);
        })
        .duration(DURATION);
    }, [gsap]);

    useLayoutEffect(() => {
      const navFull = document.getElementById('navFull');
      const navRetract = document.getElementById('navRetract');

      if (!navFull || !navRetract) return;

      const navFullWidth = navFull?.getBoundingClientRect().width;
      const navRetractWidth = navRetract?.getBoundingClientRect().width;

      if (isRetract) {
        gsap.set(navFull, {
          marginLeft: `-${navFullWidth - navRetractWidth}px`,
          pointerEvents: 'none',
          opacity: 0,
        });

        gsap.set(navRetract, {
          marginLeft: `-${navRetractWidth}px`,
          pointerEvents: 'visible',
          opacity: 1,
        });
      } else {
        gsap.set(navRetract, {
          marginLeft: `-${navRetractWidth}px`,
          pointerEvents: 'none',
          opacity: 0,
        });
      }
    }, []);

    return (
      <nav className={classNames(`nav`, className)}>
        <div id="navFull" className={'nav__full'}>
          <div className="nav__full__present">
            <div
              onClick={() => {
                const parentUrl = SSOStore.resources?.parentUrl;
                if (parentUrl) window.location.replace(parentUrl);
              }}
              className="nav__full__present__logo"
            >
              {logo}
            </div>
            <NavCollapse onCollapse={() => setMinimize()} />
          </div>

          <div className="nav__full__items">
            {data.map((item) => (
              <Menu
                key={`${item.title}-${item.position}`}
                title={item.title}
                items={item.items}
                onNavigate={onNavigate}
                selected={window.location.pathname}
              />
            ))}
          </div>
        </div>
        <div id="navRetract" className={'nav__retract'}>
          <div className="nav__retract__present">
            <NavCollapse onCollapse={() => setFull()} />
          </div>
        </div>
      </nav>
    );
  },
);
